import React, {useContext, useEffect, useState} from 'react';
import {Redirect, useHistory, useRouteMatch} from "react-router-dom";
import {Box, Chip, Fab, Grid, Paper, TextField, Tooltip, Typography} from "@material-ui/core";
import DoneIcon from '@material-ui/icons/Done';
import {useSnackbar} from "notistack";
import {MainContext} from "contexts/main.context";
import {useClientesService} from "services/hooks";
import SubHeader from "components/helpers/subHeader/subHeader";
import {formatPrice, InputPrice} from "assets/utils";

// Campo de solo lectura para los datos básicos del cliente.
const Dato = ({label, value, xs = 12, sm = 6}) => (
    <Grid item xs={xs} sm={sm}>
        <TextField
            fullWidth
            label={label}
            value={value || '-'}
            InputProps={{readOnly: true, disableUnderline: true}}
        />
    </Grid>
);

export const ClientesEdit = () => {
    const {loggedUser} = useContext(MainContext);
    const ClientesService = useClientesService();
    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();
    const match = useRouteMatch();

    const idCliente = match.params.cliente;
    const canEdit = ["A", "O"].includes(loggedUser.role);

    const [cliente, setCliente] = useState(null);
    const [credito, setCredito] = useState('');

    const loadCliente = response => {
        const _cliente = response.data.result;
        setCliente(_cliente);
        setCredito(_cliente.credito);
    };

    useEffect(() => {
        if (!canEdit)
            return;

        ClientesService.getFicha(
            idCliente,
            loadCliente,
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    }, [idCliente]);

    const creditoInvalido = credito === '' || isNaN(credito) || parseFloat(credito) < 0;

    // Mismo criterio que la validación de pedidos: saldo y cheques en cartera suman a la exposición.
    // Se calcula con el cupo que se está editando; con cupo 0 (sin límite) no hay margen.
    const getMargen = () => {
        if (!cliente || creditoInvalido || parseFloat(credito) === 0)
            return null;
        return parseFloat(credito) - parseFloat(cliente.saldo || 0) - parseFloat(cliente.cheques || 0);
    };
    const margen = getMargen();

    const onSave = () => {
        if (creditoInvalido) {
            enqueueSnackbar('El cupo de crédito debe ser un número mayor o igual a cero.', {variant: 'error'});
            return;
        }

        ClientesService.update(
            idCliente,
            {credito: parseFloat(credito)},
            () => {
                enqueueSnackbar('Los datos del cliente se actualizaron correctamente.', {variant: 'success'});
                history.push('/clientes');
            },
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    };

    if (!canEdit)
        return <Redirect to={'/clientes'}/>;

    return (
        <Box className='niquel-clientes-edit'>
            <SubHeader
                title={'Clientes'}
                subtitle={'Editar cliente'}
            />
            {cliente && (
                <Box className='container-sm mt-3 mb-3'>
                    <Paper className='p-3 mb-3'>
                        {(cliente.id[0] === 'X' || cliente.inactivo || cliente.inhabilitado) && (
                            <Box className='d-flex align-items-center mb-2'>
                                {cliente.id[0] === 'X' && <Chip size={"small"} label={'Prospecto'} className='mr-1'/>}
                                {cliente.inactivo && <Chip size={"small"} label={'Inactivo'} className='mr-1'/>}
                                {cliente.inhabilitado && <Chip size={"small"} color={"secondary"} label={'Inhabilitado'}/>}
                            </Box>
                        )}
                        <Grid container spacing={2}>
                            <Dato label={'Razón Social'} value={cliente.razsoc} sm={6}/>
                            <Dato label={'ID'} value={cliente.id} sm={3}/>
                            <Dato label={'CUIT'} value={cliente.cuit} sm={3}/>
                            <Dato label={'Dirección'} value={cliente.direccion} sm={5}/>
                            <Dato label={'Localidad'} value={cliente.localidad} sm={3}/>
                            <Dato label={'Provincia'} value={cliente.provincia} sm={2}/>
                            <Dato label={'C.P.'} value={cliente.codigopostal} sm={2}/>
                            <Dato label={'Teléfono'} value={cliente.telefono} sm={4}/>
                            <Dato label={'Email'} value={cliente.email} sm={8}/>
                            <Dato label={'Vendedor'} value={cliente.apenom} sm={4}/>
                            <Dato label={'Clasificación'} value={cliente.clasifica} sm={4}/>
                            <Dato label={'Resp. IVA'} value={cliente.responiva} sm={4}/>
                            <Dato label={'Condición Comercial'} value={cliente.condcomercial} sm={4}/>
                            <Dato label={'Lista de Precios'} value={cliente.listaprecio} sm={4}/>
                        </Grid>
                    </Paper>

                    <Paper className='p-3'>
                        <Typography variant={"h6"} className='mb-2'>Crédito</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    style={{width: '80%'}}
                                    label={'Cupo de Crédito'}
                                    value={credito}
                                    onChange={event => setCredito(event.target.value)}
                                    error={creditoInvalido}
                                    helperText={'0 = sin límite'}
                                    InputProps={{inputComponent: InputPrice}}
                                />
                            </Grid>
                            <Dato label={'Saldo Cuenta Corriente'} value={formatPrice(cliente.saldo, true)} sm={3}/>
                            <Dato label={'Cheques en Cartera'} value={formatPrice(cliente.cheques, true)} sm={3}/>
                            {/* Separado del resto: es el resultado de la cuenta, no un dato más. */}
                            <Grid item xs={12} sm={3} style={{paddingLeft: 40}}>
                                <TextField
                                    fullWidth
                                    label={'Margen de Crédito'}
                                    value={creditoInvalido ? '-' : margen === null ? 'Sin límite' : formatPrice(margen, true)}
                                    helperText={'Cupo - saldo - cheques'}
                                    InputProps={{
                                        readOnly: true,
                                        disableUnderline: true,
                                        style: {color: margen === null ? undefined : margen < 0 ? '#d32f2f' : '#388e3c', fontWeight: 500}
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Box className='niquel-fab-btn'>
                        <Tooltip title={'Guardar Cambios'}>
                            <Fab color="primary" aria-label="Guardar" onClick={onSave}>
                                <DoneIcon/>
                            </Fab>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
